import { FilterState } from '../filter-bar/filter-bar.component';
import { QUICK_PRESETS, calculatePreviousPeriod, calculateSamePeriodLastYear, formatDisplayDate } from '../date-range-picker/date-range-picker.component';
import { toIsoDate } from './analytics-request.util';

// Mirrors FilterState, but with Dates reduced to the pieces actually worth persisting: a FIXED
// period stores literal ISO dates; a RELATIVE one stores only the preset key and is recomputed
// from "today" on every open (same QUICK_PRESETS a user picks from live). The compare period
// needs no separate fixed/relative flag - compareType already carries that (previous_period/
// same_period_last_year recompute from the main range, custom is a literal stored range, none is
// nothing to store).
export interface SavedViewFilters {
	periodType: 'FIXED' | 'RELATIVE';
	presetKey?: string;
	startDate?: string;
	endDate?: string;
	compareType: 'previous_period' | 'same_period_last_year' | 'custom' | 'none';
	compareStartDate?: string;
	compareEndDate?: string;
	brands: string[];
	brandRole: 'all' | 'client' | 'competition';
	mediaTypes: string[];
	mediaOutlets: string[];
	authors: string[];
	topicGroup: 'all' | 'Industry' | 'Corporate';
	topics: string[];
	sourceTypes: string[];
	sentiments: string[];
}

// A custom (hand-picked) date range is stored as datePresetKey = 'custom' by
// DateRangePickerComponent.applyRange() when no quick preset matched - a truthy string, so a
// plain `!!datePresetKey` check wrongly treats it as a real, recomputable-from-today preset.
// Custom period lost on screen navigation (2026-11-01): every periodType = FIXED/RELATIVE
// decision across Saved Views/URL must go through this, not re-check datePresetKey truthiness
// itself - deserializeFilterState below falls back to today/today when asked to "recompute" a
// preset named 'custom' (QUICK_PRESETS has no such key), which is exactly what silently reset a
// custom range to today while merely switching screens (filterStateToQueryParams/
// filterStateFromQueryParams run on every navigation, not just Save View).
export function isRelativeDatePreset(datePresetKey: string | undefined): boolean {
	return !!datePresetKey && datePresetKey !== 'custom';
}

// periodType is the explicit choice made in the Save View modal - independent of whatever preset
// happens to be active, so a user can pin today's "Last 30 days" as a fixed historical window, or
// treat manually-picked dates as the seed of a since-forgotten preset (not possible: FIXED is the
// only option then, enforced by the modal disabling RELATIVE when filters.datePresetKey is unset
// or 'custom').
// Extracted from serializeFilterState so Задача 8's URL query-param encoding (url-filter.util.ts)
// can reuse the exact same field mapping instead of a second hand-copy.
export function filterStateToSavedView(filters: FilterState, periodType: 'FIXED' | 'RELATIVE'): SavedViewFilters {
	return {
		periodType,
		presetKey: periodType === 'RELATIVE' ? filters.datePresetKey : undefined,
		startDate: periodType === 'FIXED' && filters.startDate ? toIsoDate(filters.startDate) : undefined,
		endDate: periodType === 'FIXED' && filters.endDate ? toIsoDate(filters.endDate) : undefined,
		compareType: filters.compareType || 'none',
		compareStartDate: filters.compareType === 'custom' && filters.compareStartDate ? toIsoDate(filters.compareStartDate) : undefined,
		compareEndDate: filters.compareType === 'custom' && filters.compareEndDate ? toIsoDate(filters.compareEndDate) : undefined,
		brands: filters.brands,
		brandRole: filters.brandRole,
		mediaTypes: filters.mediaTypes,
		mediaOutlets: filters.mediaOutlets,
		authors: filters.authors,
		topicGroup: filters.topicGroup,
		topics: filters.topics,
		sourceTypes: filters.sourceTypes,
		sentiments: filters.sentiments,
	};
}

export function serializeFilterState(filters: FilterState, periodType: 'FIXED' | 'RELATIVE'): string {
	return JSON.stringify(filterStateToSavedView(filters, periodType));
}

function parseIsoDate(iso: string): Date {
	const [y, m, d] = iso.split('-').map(Number);
	return new Date(y, m - 1, d);
}

// Recomputes RELATIVE periods from today (real "today", not whenever the view was saved) before
// returning a ready-to-use FilterState - same rule a user re-selecting that same quick preset
// today would get. This is a free function (no DI), so lang/vsLabel/noComparisonLabel/translateFn
// come from the caller's own TranslateService; English defaults only matter if a future caller
// forgets to pass them.
export function deserializeFilterState(
	json: string,
	lang?: string,
	vsLabel = 'vs',
	noComparisonLabel = 'No comparison',
	translateFn: (key: string) => string = (k) => k,
): FilterState {
	const saved: SavedViewFilters = JSON.parse(json);

	let startDate: Date;
	let endDate: Date;
	let dateRange: string;
	let datePresetKey: string | undefined;

	if (saved.periodType === 'RELATIVE' && saved.presetKey) {
		const preset = QUICK_PRESETS.find((p) => p.key === saved.presetKey);
		const range = preset ? preset.getRange() : { start: new Date(), end: new Date() };
		startDate = range.start;
		endDate = range.end;
		dateRange = preset ? translateFn(preset.labelKey) : `${formatDisplayDate(startDate, lang)} – ${formatDisplayDate(endDate, lang)}`;
		datePresetKey = saved.presetKey;
	} else {
		startDate = saved.startDate ? parseIsoDate(saved.startDate) : new Date();
		endDate = saved.endDate ? parseIsoDate(saved.endDate) : new Date();
		dateRange = `${formatDisplayDate(startDate, lang)} – ${formatDisplayDate(endDate, lang)}`;
		datePresetKey = undefined;
	}

	let compareStartDate: Date | undefined;
	let compareEndDate: Date | undefined;
	let comparePeriod = noComparisonLabel;
	const compareType = saved.compareType;

	if (compareType === 'previous_period') {
		const prev = calculatePreviousPeriod(startDate, endDate, lang, vsLabel);
		compareStartDate = prev.start;
		compareEndDate = prev.end;
		comparePeriod = prev.label;
	} else if (compareType === 'same_period_last_year') {
		const prev = calculateSamePeriodLastYear(startDate, endDate, lang, vsLabel);
		compareStartDate = prev.start;
		compareEndDate = prev.end;
		comparePeriod = prev.label;
	} else if (compareType === 'custom' && saved.compareStartDate && saved.compareEndDate) {
		compareStartDate = parseIsoDate(saved.compareStartDate);
		compareEndDate = parseIsoDate(saved.compareEndDate);
		comparePeriod = `${vsLabel} ${formatDisplayDate(compareStartDate, lang)} – ${formatDisplayDate(compareEndDate, lang)}`;
	}

	return {
		dateRange,
		comparePeriod,
		startDate,
		endDate,
		datePresetKey,
		compareType,
		compareStartDate,
		compareEndDate,
		brands: saved.brands || [],
		brandRole: saved.brandRole || 'all',
		mediaTypes: saved.mediaTypes || [],
		mediaOutlets: saved.mediaOutlets || [],
		authors: saved.authors || [],
		topicGroup: saved.topicGroup || 'all',
		topics: saved.topics || [],
		sourceTypes: saved.sourceTypes || [],
		sentiments: saved.sentiments || [],
	};
}
